import _ from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { ActivityIcon, GridIcon, HomeIcon, SearchIcon, TargetIcon } from '@treams/treams-icons';
import { Input, Label, Lazy, Sidebar, Skeleton, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import Drawer from './Drawer';
import useViewer from '../packages/kakapo-ui-application/hooks/useViewer';

function Team(props: {
	group: graphql.Group;
	users?: (graphql.User & { groupID: string })[];
	query: string;
}) {
	const { t } = useTranslation();

	const users = props.users?.filter((user) => user.groupID === props.group.id);

	if (users && users.length === 0) {
		return null;
	}

	return (
		<Box mt={3}>
			<Subheading>{props.group.name}</Subheading>
			{users ? (
				users.length === 0 ? (
					<Label textAlign="center" mt={2}>
						{t('sidebar.manager.group.empty')}
					</Label>
				) : (
					_.chunk(users, 10).map((page, i) => (
						<Lazy key={i}>
							{page.map((user) => (
								<Sidebar.User
									key={user.id}
									to={`/users/${user.id}`}
									user={user}
									query={props.query}
								/>
							))}
						</Lazy>
					))
				)
			) : (
				<Skeleton.Repeat count={3}>
					<Sidebar.User.Skeleton />
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

function TeamSkeleton() {
	return (
		<Box mt={3}>
			<Subheading.Skeleton />
		</Box>
	);
}

function TeamsOverview() {
	const { t } = useTranslation();

	const viewer = useViewer();
	const roles = graphql.useViewerGroupsQuery();
	const [query, setQuery] = useState('');
	const now = useRef(Date.now()).current;

	const filters = [
		'isArchived:false',
		`activatedAt <= ${now}`,
		'(' + ['deactivatedAt = 0', `deactivatedAt > ${now}`].join(' OR ') + ')',
	].join(' AND ');

	const { results, loading } = useSearch(query, { filters, excludeViewer: true });

	const groups = _.uniqBy(
		_.sortBy(
			roles.data?.viewer.roles.flatMap((role) => {
				if (role.targetV2?.__typename === 'Group') {
					return role.targetV2 as graphql.Group;
				} else {
					return [];
				}
			}) ?? [],
			'name',
		),
		'id',
	);

	const people = _.uniq(
		roles.data?.viewer.roles.flatMap((role) => {
			if (role.targetV2?.__typename === 'User' && role.targetV2.id !== viewer?.id) {
				return role.targetV2.id;
			} else {
				return [];
			}
		}) ?? [],
	);

	const sortedResults = _.sortBy(results, (result) =>
		_.deburr(`${result.firstName} ${result.lastName}`),
	);

	const sortedPeople = sortedResults.filter(({ id }) => people.includes(id));

	return (
		<Box>
			<Input
				search
				icon={<SearchIcon />}
				placeholder={t('sidebar.manager.search.placeholder')}
				loading={loading}
				value={query ? query : undefined}
				onChange={setQuery}
			/>
			{roles.data ? (
				<>
					{sortedPeople.length > 0 && (
						<Box mt={3}>
							<Subheading>{t('sidebar.manager.group.oneOnOne')}</Subheading>
							{sortedPeople.map((person) => (
								<Sidebar.User
									key={person.id}
									to={`/users/${person.id}`}
									user={person}
									query={query}
								/>
							))}
						</Box>
					)}
					{groups?.map((group) => (
						<Team key={group.id} group={group} users={sortedResults} query={query} />
					))}
				</>
			) : (
				<Skeleton.Repeat count={3}>
					<TeamSkeleton />
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

export default function HelicopterSidebar(props: { visible?: boolean; onClose?: () => void }) {
	const { t } = useTranslation();

	const hasGoals = useFeature('2021.goals');
	const hasTalentReviews = useFeature('2021.talentReviews');
	const { isWorkspaceSuperuser, isGroupManager } = useViewerRoles();
	const hasSearchOnSkills = useFeature('2023.searchOnSkills');

	return (
		<Drawer position={props.visible ? 'active' : 'right'} onClose={props.onClose}>
			<Sidebar.Item icon={<HomeIcon />} title={t('sidebar.startpage')} path={['/manager']} small />
			{hasGoals && (
				<Sidebar.Item
					icon={<TargetIcon />}
					title={t('sidebar.collectiveGoals')}
					path="/goals/collective"
					small
				/>
			)}
			{hasTalentReviews && (isWorkspaceSuperuser || isGroupManager) && (
				<Sidebar.Item
					icon={<GridIcon />}
					title={t('sidebar.talentReview')}
					path="/talent-reviews"
					small
				/>
			)}
			<Sidebar.Item
				icon={<ActivityIcon />}
				title={t('sidebar.analytics')}
				path={
					isWorkspaceSuperuser
						? '/analytics/v2'
						: isGroupManager
						? '/analytics/department'
						: '/analytics/employee'
				}
				small
			/>

			{hasSearchOnSkills && isWorkspaceSuperuser && (
				<Sidebar.Item
					icon={<SearchIcon />}
					title={t('sidebar.competencySearch')}
					path="/competencies/search"
					small
				/>
			)}

			<Sidebar.Separator />
			<TeamsOverview />
		</Drawer>
	);
}
